import { isPrometheusConfigured } from '@/shared/config/prometheus-config';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { monitoringKeys } from '@/shared/lib/query/queryKeys';

import { PROMETHEUS_QUERIES, queryInstant, REFETCH_INTERVAL, STALE_TIME } from '@/features/monitoring/api';
import type { PodOverviewData, PrometheusVectorResult } from '@/features/monitoring/api';

export const usePodOverview = () => {
  const { data: metricsData, isLoading } = useQueryWithBoundary({
    queryKey: monitoringKeys.podOverview(),
    queryFn: async () => {
      const [
        cpuUsageData,
        cpuRequestsData,
        cpuLimitsData,
        memoryUsageData,
        memoryRequestsData,
        memoryLimitsData,
        memoryRssData,
        memoryCacheData,
        memorySwapData,
      ] = await Promise.all([
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.CPU_USAGE),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.CPU_REQUESTS),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.CPU_LIMITS),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.MEMORY_USAGE),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.MEMORY_REQUESTS),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.MEMORY_LIMITS),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.MEMORY_RSS),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.MEMORY_CACHE),
        queryInstant(PROMETHEUS_QUERIES.POD_OVERVIEW.MEMORY_SWAP),
      ]);

      return {
        cpuUsageData,
        cpuRequestsData,
        cpuLimitsData,
        memoryUsageData,
        memoryRequestsData,
        memoryLimitsData,
        memoryRssData,
        memoryCacheData,
        memorySwapData,
      };
    },
    enabled: isPrometheusConfigured(),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    ...DEFAULT_QUERY_OPTIONS,
  });

  if (isLoading || !metricsData) {
    return { data: [], isLoading: true };
  }

  const {
    cpuUsageData,
    cpuRequestsData,
    cpuLimitsData,
    memoryUsageData,
    memoryRequestsData,
    memoryLimitsData,
    memoryRssData,
    memoryCacheData,
    memorySwapData,
  } = metricsData;

  const podMap = new Map<string, Partial<PodOverviewData>>();

  const addDataToMap = (results: PrometheusVectorResult[] | undefined, field: keyof PodOverviewData) => {
    if (!results || !Array.isArray(results) || results.length === 0) {
      return;
    }

    results.forEach((result) => {
      if (!result?.metric?.pod) {
        return;
      }

      const pod = result.metric.pod;
      const value = parseFloat(result.value[1]);
      const timestamp = result.value[0];

      if (!podMap.has(pod)) {
        podMap.set(pod, { pod, timestamp });
      }

      const podData = podMap.get(pod)!;
      podData[field] = (isNaN(value) ? null : value) as never;
    });
  };

  addDataToMap(cpuUsageData, 'cpuUsage');
  addDataToMap(cpuRequestsData, 'cpuRequests');
  addDataToMap(cpuLimitsData, 'cpuLimits');
  addDataToMap(memoryUsageData, 'memoryUsage');
  addDataToMap(memoryRequestsData, 'memoryRequests');
  addDataToMap(memoryLimitsData, 'memoryLimits');
  addDataToMap(memoryRssData, 'memoryRss');
  addDataToMap(memoryCacheData, 'memoryCache');
  addDataToMap(memorySwapData, 'memorySwap');

  const data: PodOverviewData[] = Array.from(podMap.values()).map((pod) => ({
    pod: pod.pod || '',
    timestamp: pod.timestamp || Math.floor(Date.now() / 1000),
    cpuUsage: pod.cpuUsage ?? null,
    cpuRequests: pod.cpuRequests ?? null,
    cpuRequestsPercent:
      pod.cpuUsage != null && pod.cpuRequests != null && pod.cpuRequests > 0
        ? (pod.cpuUsage / pod.cpuRequests) * 100
        : null,
    cpuLimits: pod.cpuLimits ?? null,
    cpuLimitsPercent:
      pod.cpuUsage != null && pod.cpuLimits != null && pod.cpuLimits > 0 ? (pod.cpuUsage / pod.cpuLimits) * 100 : null,
    memoryUsage: pod.memoryUsage ?? null,
    memoryRequests: pod.memoryRequests ?? null,
    memoryRequestsPercent:
      pod.memoryUsage != null && pod.memoryRequests != null && pod.memoryRequests > 0
        ? (pod.memoryUsage / pod.memoryRequests) * 100
        : null,
    memoryLimits: pod.memoryLimits ?? null,
    memoryLimitsPercent:
      pod.memoryUsage != null && pod.memoryLimits != null && pod.memoryLimits > 0
        ? (pod.memoryUsage / pod.memoryLimits) * 100
        : null,
    memoryRss: pod.memoryRss ?? null,
    memoryCache: pod.memoryCache ?? null,
    memorySwap: pod.memorySwap ?? null,
  }));

  return { data, isLoading: false };
};
